import Avatar from '@/components/Avatar'
import IcoMoon from '@/components/IcoMoon'
import Icon from '@/components/Icon'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import { useNavigation } from '@/context/navigation'
import { UserMenuNavigationItem } from '@/shared/api-generated-types'
import UserCardNext from '@/widgets/UserCardNext'
import { useClickOutside } from '@react-hookz/web'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRef, useState } from 'react'
import * as styles from './style'

export interface Props {
  firstName?: string
  fullName?: string
  userMenuLinks?: UserMenuNavigationItem[]
}

const UserMenu = ({ firstName, fullName, userMenuLinks }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const { dispatch } = useAuth()
  const userPopoutRef = useRef(null)
  const [userPopoutVisible, setUserPopoutVisible] = useState(false)
  const canSeeClassNotes = useFeatureToggle('eng_class_notes')
  const showHelenTab = useFeatureToggle('helen_tab')
  const {
    state: { searchBox },
    dispatch: dispatchNavigation,
  } = useNavigation()

  useClickOutside(userPopoutRef, toggleUserPopout)

  function toggleUserPopout(): void {
    setUserPopoutVisible(!userPopoutVisible)
  }

  function hideSearchMenu(): void {
    if (searchBox.open) {
      dispatchNavigation({ type: 'SET_SEARCH_OPEN', payload: false })
    }
  }

  const renderMenuLink = (link: UserMenuNavigationItem): JSX.Element | null => {
    switch (link.id) {
      case 'nav:classNotes':
        return canSeeClassNotes ? (
          <li key={link.id}>
            <Link href="/notes" prefetch={false} onClick={hideSearchMenu}>
              <IcoMoon icon="notes" size={16} className="mr-xxs" />
              {t('nav.classNotes')}
            </Link>
          </li>
        ) : null
      case 'nav:mySchedule':
        return (
          <li key={link.id}>
            <Link href="/schedule/my-schedule" prefetch={false} onClick={hideSearchMenu}>
              <IcoMoon icon="calendar" size={16} className="mr-xxs" />
              {t('nav.mySchedule')}
            </Link>
          </li>
        )
      case 'nav:reminders':
        return showHelenTab ? (
          <li key={link.id}>
            <Link href="/reminders" prefetch={false} onClick={hideSearchMenu}>
              <IcoMoon icon="alarm" size={16} className="mr-xxs" />
              {t('nav.reminders')}
            </Link>
          </li>
        ) : null
      default:
        return null
    }
  }

  return (
    <>
      <div onClick={toggleUserPopout} className={styles.root}>
        <UserCardNext
          avatar={<Avatar title={fullName} size="sm" />}
          theme="primaryNext"
          title={firstName}
          iconRight={
            <Icon className={styles.chevron} name={userPopoutVisible ? 'chevron-black-up' : 'chevron-black-down'} />
          }
        />
      </div>
      {userPopoutVisible && (
        <div className={styles.userPopout}>
          <nav ref={userPopoutRef}>
            <ul>
              {(userMenuLinks || [])?.map(renderMenuLink)}
              <li>
                <Link href="/my-bookmarks" prefetch={false} onClick={hideSearchMenu}>
                  <IcoMoon icon="heart" size={16} className="mr-xxs" />
                  {t('nav.myBookmarks')}
                </Link>
              </li>
              <li>
                <Link href="/account-settings" prefetch={false} onClick={hideSearchMenu}>
                  <IcoMoon icon="settings" size={16} className="mr-xxs" />
                  {t('nav.settings')}
                </Link>
              </li>
              <li>
                <Link href="#" onClick={() => dispatch({ type: 'LOGOUT', payload: { returnHome: true } })}>
                  <IcoMoon icon="logout" size={16} className="mr-xxs" />
                  {t('nav.logOut')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </>
  )
}
export default UserMenu
